import React, { useState, useEffect, memo } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import VideoPlayer from "@/components/VideoPlayer";
import { useVideoPlaybackSafe } from "@/contexts/VideoPlaybackContext";

interface PromoVideoPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubscribe: () => void;
}

const CountdownTimer = memo(({ timeLeft }: { timeLeft: number }) => (
  <div className="absolute top-4 right-4 z-20 bg-black/70 text-white px-3 py-2 rounded-md text-sm font-medium">
    Fechar em {timeLeft}s
  </div>
));
CountdownTimer.displayName = "CountdownTimer";

const PromoVideoPopup: React.FC<PromoVideoPopupProps> = ({ isOpen, onClose, onSubscribe }) => {
  const [canClose, setCanClose] = useState(false);
  const [timeLeft, setTimeLeft] = useState(7);
  const videoPlayback = useVideoPlaybackSafe();

  useEffect(() => {
    if (!isOpen) return;
    setCanClose(false);
    setTimeLeft(7);
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setCanClose(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-full p-0 bg-background/95">
        <DialogTitle className="sr-only">Oferta Exclusiva de Planos</DialogTitle>
        <DialogDescription className="sr-only">Vídeo promocional apresentando os planos disponíveis</DialogDescription>

        <div className="relative">
          <div className="aspect-video w-full bg-black/20 relative">
            {isOpen && (
              <VideoPlayer
                src="https://assets.loverslabs.art/popuploverslabs/videoplans.mp4"
                controls
                className="object-cover rounded-t-lg"
                isPaused={videoPlayback?.shouldPauseBackgroundVideos ?? false}
              />
            )}
          </div>

          <div className="p-6 md:p-8 text-center bg-gradient-to-br from-red/5 via-gold/5 to-background">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-premium border-premium mb-4 opacity-90 hover:opacity-100 transition-opacity">
              <Sparkles
                className="w-4 h-4"
                style={{
                  color: "hsl(var(--gold))",
                }}
              />
              <span
                className="text-sm font-medium"
                style={{
                  color: "hsl(var(--gold))",
                }}
              >
                Exclusive Offer
              </span>
            </div>
            <h3 className="text-3xl font-bold mb-3 text-gold">Supreme Offer</h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">Unlock the full potential of NSFW art</p>
            <Button variant="gradient" size="lg" className="shadow-elegant px-8" onClick={onClose}>
              <Sparkles className="w-4 h-4 mr-2" />
              Unlock
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
export default memo(PromoVideoPopup);
